// src/utils/cubbyManagerReport.js
// ---------------------------------------------------------------------------
// Cubby Manager — report logic + PDF builder.
//
//   buildPhoneReport(records, studentsById)  -> { rows, summary }
//   buildPhoneReportPdf({ date, report, generatedBy }) -> jsPDF instance
//
// Pure functions only (no Firebase, no React) so the numbers on screen and the
// numbers in the PDF always come from the same place.
//
// PDF is imperative jsPDF via a PDFDoc helper — never html2canvas / doc.html().
// ---------------------------------------------------------------------------

import { jsPDF } from 'jspdf';

/* ------------------------------------------------------------------------- */
/* Config                                                                    */
/* ------------------------------------------------------------------------- */

// Statuses on /phoneCheckIn/{date}/records/* that mean "did not turn in".
// Compared lowercase with punctuation stripped, so "notTurnedIn",
// "not_turned_in" and "Not Turned In" all match. The kiosk writes "missing".
// "pending" is deliberately NOT a fail (same decision as the MOPs engine).
export const FAIL_STATUSES = ['missing', 'notturnedin'];
// Written by the phoneAbsences reconcile (or a manual mark): the student was
// absent from school, so a missing phone is expected and NOT a fail.
export const ABSENT_STATUSES = ['absent'];
// Student has parental permission to leave the phone at home
// (/students/{sisId}.phoneExempt === true). Never a fail.
export const EXEMPT_STATUSES = ['exempt'];

export const COHORT_ORDER = ['Day', 'Boarding', 'Unknown'];

/* ------------------------------------------------------------------------- */
/* Student field helpers (tolerant of the different shapes on /students)     */
/* ------------------------------------------------------------------------- */

const str = (v) => (v === null || v === undefined ? '' : String(v).trim());

export const normStatus = (v) => str(v).toLowerCase().replace(/[^a-z]/g, '');
export const isFailStatus = (v) => FAIL_STATUSES.includes(normStatus(v));
export const isAbsentStatus = (v) => ABSENT_STATUSES.includes(normStatus(v));
export const isExemptStatus = (v) => EXEMPT_STATUSES.includes(normStatus(v));
export const isPhoneExempt = (s) => !!s && (s.phoneExempt === true || str(s.phoneExempt).toLowerCase() === 'true');

// Why a student is exempt. `phoneExempt` stays the one boolean every consumer
// checks; `phonePolicy` is the human reason shown on cards and reports.
export const PHONE_POLICIES = [
  { id: 'required', label: 'Required', short: 'required', exempt: false, help: 'Turns in a phone every day.' },
  { id: 'leaveAtHome', label: 'Leaves phone at home', short: 'leaves at home', exempt: true, help: 'Parental permission to leave the phone at home.' },
  { id: 'noPhone', label: 'No phone', short: 'no phone', exempt: true, help: 'Parents report the student does not carry a phone.' },
  { id: 'withdrawn', label: 'Withdrawn / left school', short: 'withdrawn', exempt: true, help: 'No longer enrolled; roster not yet updated.' },
];
export function phonePolicyOf(s) {
  const id = str(s && s.phonePolicy);
  const hit = PHONE_POLICIES.find((p) => p.id === id);
  if (hit) return hit;
  return isPhoneExempt(s) ? PHONE_POLICIES[1] : PHONE_POLICIES[0]; // legacy: exempt with no reason
}

export function studentName(s) {
  if (!s) return '';
  const direct = str(s.fullName) || str(s.name) || str(s.displayName) || str(s.studentName);
  if (direct) return direct;
  return [str(s.firstName), str(s.lastName)].filter(Boolean).join(' ');
}

export function sortName(s) {
  if (!s) return '';
  const last = str(s.lastName);
  const first = str(s.firstName);
  if (last) return `${last} ${first}`.toLowerCase();
  return studentName(s).toLowerCase();
}

export function cohortOf(s) {
  if (!s) return 'Unknown';
  const fields = ['cohort', 'residentStatus', 'currentResidentStatus', 'CurrentResidentStatus'];
  for (const f of fields) {
    const v = str(s[f]).toLowerCase();
    if (!v) continue;
    if (v.includes('board')) return 'Boarding'; // check first: "5-day boarding"
    if (v.includes('day')) return 'Day';
  }
  if (s.boarding === true) return 'Boarding';
  if (s.boarding === false) return 'Day';
  return 'Unknown';
}

export function gradeNum(s) {
  if (!s) return null;
  const m = str(s.grade).match(/(\d{1,2})/);
  return m ? Number(m[1]) : null;
}

export function gradeLabel(s) {
  const n = gradeNum(s);
  if (n !== null) return `Grade ${n}`;
  return str(s && s.grade) || 'Unknown';
}

export function advisorOf(s) {
  if (!s) return '';
  return str(s.advisorName) || str(s.advisor);
}

export function cubbyOf(s) {
  return s ? str(s.cubby) : '';
}

// A student must be POSITIVELY active to count: `status` is authoritative when
// present ("active" only — "inactive", "withdrawn", "graduated"... all fail);
// if `status` is absent, fall back to `active` (true / "true"). A doc with
// neither field is treated as inactive.
export function isActiveStudent(s) {
  if (!s) return false;
  const status = str(s.status).toLowerCase();
  if (status) return status === 'active';
  const a = s.active;
  if (a === undefined || a === null || str(a) === '') return false;
  return a === true || str(a).toLowerCase() === 'true';
}

// Natural sort so cubby "9" comes before "10" and "A12" before "B3".
export function compareCubby(a, b) {
  return str(a).localeCompare(str(b), undefined, { numeric: true, sensitivity: 'base' });
}

/* ------------------------------------------------------------------------- */
/* Report                                                                    */
/* ------------------------------------------------------------------------- */

/**
 * records:       [{ _id, studentId, status, ... }] from /phoneCheckIn/{date}/records
 * studentsById:  Map<sisId string, student doc>
 */
export function buildPhoneReport(records, studentsById) {
  const statusCounts = {};
  const seen = new Set();
  const rows = [];
  const absentRows = [];
  const exemptRows = [];

  for (const r of records || []) {
    const rawStatus = str(r.status) || '(no status)';
    statusCounts[rawStatus] = (statusCounts[rawStatus] || 0) + 1;
    const fail = isFailStatus(r.status);
    const absent = isAbsentStatus(r.status);
    const exemptStatus = isExemptStatus(r.status);
    if (!fail && !absent && !exemptStatus) continue;

    const id = str(r.studentId) || str(r.personId) || str(r.sisId) || str(r._id);
    if (!id || seen.has(id)) continue;
    seen.add(id);

    const s = (studentsById && studentsById.get(id)) || null;
    // A fail for a phone-exempt student is reclassified on the fly, even if
    // the nightly job has not yet rewritten the record.
    const exempt = exemptStatus || (fail && isPhoneExempt(s));

    // Prefer the live /students doc; fall back to whatever the record carries.
    let cohort = cohortOf(s);
    if (cohort === 'Unknown') cohort = cohortOf(r);
    const gSrc = gradeNum(s) !== null || !gradeNum(r) ? s || r : r;

    const row = {
      id,
      recordId: str(r._id),
      resolved: !!s,
      name: studentName(s) || studentName(r) || str(r.person) || `Unknown student (${id})`,
      sortName: sortName(s) || sortName(r) || id,
      cohort,
      grade: gradeNum(gSrc),
      gradeLabel: gradeLabel(gSrc),
      cubby: cubbyOf(s) || cubbyOf(r),
      advisor: advisorOf(s) || advisorOf(r),
      status: rawStatus,
      // populated on absent rows by the reconcile
      priorStatus: str(r.priorStatus),
      absentBlocks: r.absentBlocks ?? null,
      absentOfBlocks: r.absentOfBlocks ?? null,
      absenceStatuses: Array.isArray(r.absenceStatuses) ? r.absenceStatuses : [],
      markedBy: str(r.absentReconciledBy),
      manualMark: !!r.absentReconciledBy && !String(r.absentReconciledBy).startsWith('system:'),
      exemptNote: str(s && s.phoneExemptNote),
      exemptPolicy: s ? phonePolicyOf(s).label : '',
    };
    if (exempt) exemptRows.push(row);
    else if (absent) absentRows.push(row);
    else rows.push(row);
  }

  const byCohortGradeName = (a, b) => {
    const c = COHORT_ORDER.indexOf(a.cohort) - COHORT_ORDER.indexOf(b.cohort);
    if (c) return c;
    const g = (a.grade === null ? 99 : a.grade) - (b.grade === null ? 99 : b.grade);
    if (g) return g;
    return a.sortName.localeCompare(b.sortName);
  };
  rows.sort(byCohortGradeName);
  absentRows.sort(byCohortGradeName);
  exemptRows.sort(byCohortGradeName);

  const byCohort = { Day: 0, Boarding: 0, Unknown: 0 };
  const gradeMap = new Map();
  for (const row of rows) {
    byCohort[row.cohort] += 1;
    if (!gradeMap.has(row.gradeLabel)) {
      gradeMap.set(row.gradeLabel, {
        label: row.gradeLabel, grade: row.grade, Day: 0, Boarding: 0, Unknown: 0, total: 0,
      });
    }
    const g = gradeMap.get(row.gradeLabel);
    g[row.cohort] += 1;
    g.total += 1;
  }
  const byGrade = Array.from(gradeMap.values()).sort(
    (a, b) => (a.grade === null ? 99 : a.grade) - (b.grade === null ? 99 : b.grade)
  );

  const totalRecords = (records || []).length;
  // Absent students are not "expected to turn in", so the rate excludes them.
  const expected = totalRecords - absentRows.length - exemptRows.length;
  return {
    rows,
    absentRows,
    exemptRows,
    summary: {
      totalRecords,
      expected,
      failed: rows.length,
      failedPct: expected ? Math.round((rows.length / expected) * 100) : 0,
      absent: absentRows.length,
      exempt: exemptRows.length,
      byCohort,
      byGrade,
      statusCounts,
    },
  };
}

/* ------------------------------------------------------------------------- */
/* Dates (Asia/Amman, permanently UTC+3)                                     */
/* ------------------------------------------------------------------------- */

export function todayAmman() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Amman' }).format(new Date());
}

const asUtcNoon = (ymd) => new Date(`${ymd}T12:00:00Z`);

export function longDate(ymd) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd || '')) return ymd || '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }).format(asUtcNoon(ymd));
}

// Step to the previous/next school day (Sun–Thu), skipping Fri + Sat.
export function shiftSchoolDay(ymd, dir) {
  const d = asUtcNoon(ymd);
  do {
    d.setUTCDate(d.getUTCDate() + dir);
  } while (d.getUTCDay() === 5 || d.getUTCDay() === 6);
  return d.toISOString().slice(0, 10);
}

/* ------------------------------------------------------------------------- */
/* PDF                                                                       */
/* ------------------------------------------------------------------------- */

const NAVY = [35, 60, 95]; // #233C5F
const GOLD = [162, 145, 100]; // #A29164
const RED = [232, 53, 48]; // #E83530
const WASH = [244, 246, 249]; // light grey-blue zebra rows / tiles
const INK = [40, 44, 52];
const MUTED = [110, 116, 128];

// Standard jsPDF fonts are WinAnsi only — keep text inside that range.
function safe(v) {
  return str(v)
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[^\x20-\xFF]/g, '?');
}

class PDFDoc {
  constructor({ title, subtitle }) {
    this.doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    this.W = 210;
    this.H = 297;
    this.M = 14;
    this.bottom = this.H - 18;
    this.title = title;
    this.subtitle = subtitle;
    this.y = 0;
    this.drawCover();
  }

  get innerW() {
    return this.W - this.M * 2;
  }

  fill(c) { this.doc.setFillColor(c[0], c[1], c[2]); }
  stroke(c) { this.doc.setDrawColor(c[0], c[1], c[2]); }
  color(c) { this.doc.setTextColor(c[0], c[1], c[2]); }
  font(style, size) { this.doc.setFont('helvetica', style); this.doc.setFontSize(size); }

  drawCover() {
    const h = 34;
    this.fill(NAVY);
    this.doc.rect(0, 0, this.W, h, 'F');
    this.fill(GOLD);
    this.doc.rect(0, h, this.W, 1.4, 'F');

    this.color([255, 255, 255]);
    this.font('bold', 20);
    this.doc.text(safe(this.title), this.M, 15);
    this.color([224, 214, 184]);
    this.font('normal', 11);
    this.doc.text(safe(this.subtitle), this.M, 23);
    this.font('normal', 8.5);
    this.doc.text("King's Academy  |  Cubby Manager", this.M, 29.5);
    this.y = h + 10;
  }

  drawRunningHeader() {
    this.fill(NAVY);
    this.doc.rect(0, 0, this.W, 11, 'F');
    this.fill(GOLD);
    this.doc.rect(0, 11, this.W, 0.9, 'F');
    this.color([255, 255, 255]);
    this.font('bold', 9.5);
    this.doc.text(safe(this.title), this.M, 7.2);
    this.font('normal', 9);
    this.doc.text(safe(this.subtitle), this.W - this.M, 7.2, { align: 'right' });
    this.y = 19;
  }

  newPage() {
    this.doc.addPage();
    this.drawRunningHeader();
  }

  ensure(h) {
    if (this.y + h > this.bottom) {
      this.newPage();
      return true;
    }
    return false;
  }

  fit(text, maxW) {
    let t = safe(text);
    if (this.doc.getTextWidth(t) <= maxW) return t;
    while (t.length > 1 && this.doc.getTextWidth(`${t}...`) > maxW) t = t.slice(0, -1);
    return `${t}...`;
  }

  sectionTitle(text, count) {
    this.ensure(16);
    this.fill(GOLD);
    this.doc.rect(this.M, this.y - 4, 1.6, 5, 'F');
    this.color(NAVY);
    this.font('bold', 12.5);
    this.doc.text(safe(text), this.M + 5, this.y);
    if (count !== undefined) {
      const w = this.doc.getTextWidth(safe(text));
      this.color(MUTED);
      this.font('normal', 10);
      this.doc.text(`(${count})`, this.M + 7 + w, this.y);
    }
    this.y += 2.2;
    this.stroke(GOLD);
    this.doc.setLineWidth(0.4);
    this.doc.line(this.M, this.y, this.W - this.M, this.y);
    this.y += 5;
  }

  tiles(items) {
    const gap = 4;
    const w = (this.innerW - gap * (items.length - 1)) / items.length;
    const h = 24;
    this.ensure(h + 6);
    items.forEach((it, i) => {
      const x = this.M + i * (w + gap);
      this.fill(WASH);
      this.stroke(GOLD);
      this.doc.setLineWidth(0.4);
      this.doc.roundedRect(x, this.y, w, h, 2, 2, 'FD');
      this.fill(it.accent || NAVY);
      this.doc.rect(x, this.y, 1.8, h, 'F');
      this.color(it.accent || NAVY);
      this.font('bold', 22);
      this.doc.text(String(it.value), x + 7, this.y + 12.5);
      this.color(INK);
      this.font('bold', 9.5);
      this.doc.text(safe(it.label), x + 7, this.y + 18.2);
      if (it.note) {
        this.color(MUTED);
        this.font('normal', 7.8);
        this.doc.text(this.fit(it.note, w - 9), x + 7, this.y + 22);
      }
    });
    this.y += h + 8;
  }

  /**
   * cols: [{ header, w (mm), align?, bold? }]   rows: [[cell, ...]]
   * Repeats the header row after a page break.
   */
  table(cols, rows, { rowH = 6.6, totalRow = null } = {}) {
    const drawHead = () => {
      this.fill(NAVY);
      this.doc.rect(this.M, this.y, this.innerW, 7, 'F');
      this.color([255, 255, 255]);
      this.font('bold', 8.8);
      let x = this.M;
      cols.forEach((c) => {
        const tx = c.align === 'right' ? x + c.w - 2.5 : c.align === 'center' ? x + c.w / 2 : x + 2.5;
        this.doc.text(safe(c.header), tx, this.y + 4.8, { align: c.align || 'left' });
        x += c.w;
      });
      this.y += 7;
    };

    const drawRow = (cells, idx, isTotal) => {
      if (isTotal) {
        this.fill([226, 231, 239]);
        this.doc.rect(this.M, this.y, this.innerW, rowH, 'F');
      } else if (idx % 2 === 1) {
        this.fill(WASH);
        this.doc.rect(this.M, this.y, this.innerW, rowH, 'F');
      }
      let x = this.M;
      cols.forEach((c, ci) => {
        this.color(isTotal ? NAVY : INK);
        this.font(isTotal || c.bold ? 'bold' : 'normal', 9);
        const tx = c.align === 'right' ? x + c.w - 2.5 : c.align === 'center' ? x + c.w / 2 : x + 2.5;
        this.doc.text(this.fit(cells[ci], c.w - 4), tx, this.y + rowH - 2.1, { align: c.align || 'left' });
        x += c.w;
      });
      this.y += rowH;
    };

    this.ensure(7 + rowH * 2);
    drawHead();
    rows.forEach((cells, idx) => {
      if (this.ensure(rowH)) drawHead();
      drawRow(cells, idx, false);
    });
    if (totalRow) {
      if (this.ensure(rowH)) drawHead();
      drawRow(totalRow, 0, true);
    }
    this.stroke(GOLD);
    this.doc.setLineWidth(0.3);
    this.doc.line(this.M, this.y, this.W - this.M, this.y);
    this.y += 9;
  }

  paragraph(text, { size = 9.5, color = MUTED } = {}) {
    this.font('normal', size);
    this.color(color);
    const lines = this.doc.splitTextToSize(safe(text), this.innerW);
    this.ensure(lines.length * 4.6 + 2);
    this.doc.text(lines, this.M, this.y);
    this.y += lines.length * 4.6 + 3;
  }

  finish(footerLeft) {
    const n = this.doc.getNumberOfPages();
    for (let p = 1; p <= n; p += 1) {
      this.doc.setPage(p);
      this.stroke(GOLD);
      this.doc.setLineWidth(0.3);
      this.doc.line(this.M, this.H - 12, this.W - this.M, this.H - 12);
      this.font('normal', 8);
      this.color(MUTED);
      this.doc.text(safe(footerLeft), this.M, this.H - 7.5);
      this.doc.text(`Page ${p} of ${n}`, this.W - this.M, this.H - 7.5, { align: 'right' });
    }
    return this.doc;
  }
}

/**
 * report: result of buildPhoneReport().  Returns the jsPDF instance;
 * call .save(pdfFileName(date)) on it.
 */
export function buildPhoneReportPdf({ date, report, generatedBy }) {
  const { rows, summary } = report;
  const pdf = new PDFDoc({
    title: 'Phone Turn-In Report',
    subtitle: longDate(date),
  });

  const tileItems = [
    {
      value: summary.failed,
      label: 'Phones not turned in',
      note: `${summary.failedPct}% of ${summary.expected} expected`,
      accent: RED,
    },
    { value: summary.byCohort.Day, label: 'Day students', accent: NAVY },
    { value: summary.byCohort.Boarding, label: 'Boarding students', accent: NAVY },
    { value: summary.absent, label: 'Absent (excluded)', note: 'Per attendance', accent: MUTED },
  ];
  if (summary.exempt > 0) {
    tileItems.push({ value: summary.exempt, label: 'Exempt (excluded)', note: 'Permission to leave at home', accent: MUTED });
  }
  if (summary.byCohort.Unknown > 0) {
    tileItems.push({
      value: summary.byCohort.Unknown, label: 'Cohort unknown', note: 'Set in Student Roster', accent: GOLD,
    });
  }
  pdf.tiles(tileItems);

  if (summary.totalRecords === 0) {
    pdf.paragraph(
      'No phone check-in records exist for this date. It may be a weekend, a holiday, or the kiosk data has not been recorded yet.',
      { size: 11, color: INK }
    );
    return pdf.finish(footerText(generatedBy));
  }

  if (rows.length === 0) {
    pdf.paragraph('Every student who was at school turned in their phone.', {
      size: 11, color: INK,
    });
    appendAbsentSection(pdf, report.absentRows || []);
    appendExemptSection(pdf, report.exemptRows || []);
    return pdf.finish(footerText(generatedBy));
  }

  // Grade x cohort
  pdf.sectionTitle('By grade and cohort');
  const showUnknown = summary.byCohort.Unknown > 0;
  const gCols = [
    { header: 'Grade', w: showUnknown ? 62 : 80, bold: true },
    { header: 'Day', w: 30, align: 'center' },
    { header: 'Boarding', w: 30, align: 'center' },
  ];
  if (showUnknown) gCols.push({ header: 'Unknown', w: 30, align: 'center' });
  gCols.push({ header: 'Total', w: pdf.innerW - gCols.reduce((a, c) => a + c.w, 0), align: 'center', bold: true });

  const gRows = summary.byGrade.map((g) => {
    const cells = [g.label, String(g.Day), String(g.Boarding)];
    if (showUnknown) cells.push(String(g.Unknown));
    cells.push(String(g.total));
    return cells;
  });
  const gTotal = ['All grades', String(summary.byCohort.Day), String(summary.byCohort.Boarding)];
  if (showUnknown) gTotal.push(String(summary.byCohort.Unknown));
  gTotal.push(String(summary.failed));
  pdf.table(gCols, gRows, { totalRow: gTotal });

  // Name lists per cohort
  const listCols = [
    { header: '#', w: 10, align: 'right' },
    { header: 'Cubby', w: 20, align: 'center', bold: true },
    { header: 'Student', w: 72 },
    { header: 'Grade', w: 24 },
    { header: 'Advisor', w: pdf.innerW - 126 },
  ];
  COHORT_ORDER.forEach((cohort) => {
    const list = rows.filter((r) => r.cohort === cohort);
    if (!list.length) return;
    const heading = cohort === 'Unknown' ? 'Cohort unknown' : `${cohort} students`;
    pdf.sectionTitle(heading, list.length);
    pdf.table(
      listCols,
      list.map((r, i) => [String(i + 1), r.cubby || '-', r.name, r.gradeLabel, r.advisor || '-'])
    );
  });

  appendAbsentSection(pdf, report.absentRows || []);
  appendExemptSection(pdf, report.exemptRows || []);
  return pdf.finish(footerText(generatedBy));
}

// Students with parental permission to leave the phone at home.
function appendExemptSection(pdf, exemptRows) {
  if (!exemptRows.length) return;
  pdf.sectionTitle('Phone exempt - not counted', exemptRows.length);
  pdf.paragraph('No phone is expected from these students (leaves phone at home, no phone, or withdrawn).');
  const cols = [
    { header: '#', w: 10, align: 'right' },
    { header: 'Cubby', w: 20, align: 'center', bold: true },
    { header: 'Student', w: 66 },
    { header: 'Grade', w: 22 },
    { header: 'Cohort', w: 24 },
    { header: 'Reason', w: pdf.innerW - 142 },
  ];
  pdf.table(cols, exemptRows.map((r, i) => [
    String(i + 1), r.cubby || '-', r.name, r.gradeLabel, r.cohort,
    [r.exemptPolicy, r.exemptNote].filter(Boolean).join(' - ') || '-',
  ]));
}

// Students whose missing phone is explained by an attendance absence.
function appendAbsentSection(pdf, absentRows) {
  if (!absentRows.length) return;
  pdf.sectionTitle('Absent from school - not counted', absentRows.length);
  pdf.paragraph(
    'These students had no phone turned in but were recorded absent in attendance for the day, so no violation is reported.'
  );
  const cols = [
    { header: '#', w: 10, align: 'right' },
    { header: 'Cubby', w: 20, align: 'center', bold: true },
    { header: 'Student', w: 66 },
    { header: 'Grade', w: 22 },
    { header: 'Cohort', w: 24 },
    { header: 'Attendance', w: pdf.innerW - 142 },
  ];
  pdf.table(
    cols,
    absentRows.map((r, i) => [
      String(i + 1), r.cubby || '-', r.name, r.gradeLabel, r.cohort,
      r.manualMark ? 'Marked absent manually'
        : r.absentOfBlocks ? `${r.absentBlocks}/${r.absentOfBlocks} blocks absent` : 'Absent',
    ])
  );
}

function footerText(generatedBy) {
  const stamp = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Amman', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date());
  return `Generated ${stamp} (Amman)${generatedBy ? ` by ${generatedBy}` : ''}  |  Confidential - student information`;
}

export function pdfFileName(date) {
  return `PhoneReport_${date}.pdf`;
}